import { TMasterCompanyMainSkill } from "@/api/entities/master-company-main-skill.entity";
import { TMasterCompanySpecificBusinessClassification } from "@/api/entities/master-company-specific-business-classification.entity";
import { createCompanySpecificBusinessClassificationApi, updateCompanySpecificBusinessClassificationApi } from "@/api/services/global/master";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { useDialog } from "@/hooks/useDialog";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CompanyMainSkillFormTable } from "@/features/master-data/components/CompanySpecificIndustry/CompanyMainSkillFormTable";
import { useConfirmCloseModal } from "@/features/master-data/hooks/useConfirmCloseModal";
import { companySpecificBusinessClassificationSchema } from "@/features/master-data/schema/masterDataCompanySpecificIndustrySchema";
import { TCompanySpecificBusinessClassificationForm } from "@/features/master-data/types/masterDataCompanySpecificIndustryForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

const getMainSkillsSnapshot = (mainSkills: TMasterCompanyMainSkill[]) =>
  JSON.stringify(
    mainSkills.map(
      ({ id, mainSkillName, examName1Year, examGrade1Year, examNameFinal, examGradeFinal, employerChangeRestrictionPeriod, isHidden }) => [
        id,
        mainSkillName,
        examName1Year,
        examGrade1Year,
        examNameFinal,
        examGradeFinal,
        employerChangeRestrictionPeriod,
        isHidden,
      ],
    ),
  );

type TCompanySpecificBusinessClassificationFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedCompanySpecificBusinessClassification?: TMasterCompanySpecificBusinessClassification;
  existingCompanySpecificIndustry: string;
  existingCompanySpecificIndustryId: number;
  setSelectedCompanySpecificBusinessClassificationRows: React.Dispatch<React.SetStateAction<TMasterCompanySpecificBusinessClassification[]>>;
};

export const CompanySpecificBusinessClassificationFormModal: FC<TCompanySpecificBusinessClassificationFormModalProps> = ({
  onClose,
  isOpen,
  selectedCompanySpecificBusinessClassification,
  existingCompanySpecificIndustry,
  existingCompanySpecificIndustryId,
  setSelectedCompanySpecificBusinessClassificationRows,
}) => {
  const queryClient = useQueryClient();

  const [companyMainSkillRows, setCompanyMainSkillRows] = useState<TMasterCompanyMainSkill[]>(
    selectedCompanySpecificBusinessClassification?.mainSkills ?? [],
  );

  const [initialMainSkillsSnapshot] = useState(() => getMainSkillsSnapshot(companyMainSkillRows));

  // Main skills are saved by their own API right away, so mirror every change into the parent rows
  // whether this modal is saved or cancelled; otherwise reopening it would show stale main skills.
  useEffect(() => {
    if (!selectedCompanySpecificBusinessClassification) {
      return;
    }
    const classificationId = toNumber(selectedCompanySpecificBusinessClassification.id);
    setSelectedCompanySpecificBusinessClassificationRows((prev) =>
      prev.map((item) => (toNumber(item.id) === classificationId ? { ...item, mainSkills: companyMainSkillRows } : item)),
    );
  }, [companyMainSkillRows]);

  const defaultValues = useMemo(
    () => ({
      companySpecificBusinessClassificationName: selectedCompanySpecificBusinessClassification
        ? selectedCompanySpecificBusinessClassification.companySpecificBusinessClassificationName
        : undefined,
    }),
    [selectedCompanySpecificBusinessClassification],
  );

  const methods = useForm<TCompanySpecificBusinessClassificationForm>({
    resolver: yupResolver(companySpecificBusinessClassificationSchema),
    mode: "onBlur",
    defaultValues,
  });

  const {
    control,
    formState: { isDirty },
  } = methods;
  const { confirmClose, handleRequestClose } = useConfirmCloseModal(isDirty, onClose);

  const onSubmit = async (data: TCompanySpecificBusinessClassificationForm) => {
    if (!existingCompanySpecificIndustryId || !existingCompanySpecificIndustry) {
      return;
    }
    try {
      if (selectedCompanySpecificBusinessClassification) {
        const rs = await updateCompanySpecificBusinessClassificationApi(selectedCompanySpecificBusinessClassification.id, {
          companySpecificIndustryId: toNumber(existingCompanySpecificIndustryId),
          companySpecificBusinessClassificationName: data.companySpecificBusinessClassificationName,
        });
        // The rename response carries no mainSkills relation, so merge onto the existing
        // row instead of replacing it — otherwise 主たる技能 is wiped from the table.
        setSelectedCompanySpecificBusinessClassificationRows((prev) =>
          prev.map((item) =>
            toNumber(item.id) === toNumber(rs.data.id) ? { ...item, ...rs.data, mainSkills: rs.data.mainSkills ?? companyMainSkillRows } : item,
          ),
        );
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createCompanySpecificBusinessClassificationApi({
          companySpecificBusinessClassificationName: data.companySpecificBusinessClassificationName,
          companySpecificIndustryId: existingCompanySpecificIndustryId,
        });
        // The create response carries no mainSkills relation; a new classification has none yet.
        setSelectedCompanySpecificBusinessClassificationRows((prev) => [...prev, { ...rs.data, mainSkills: rs.data.mainSkills ?? [] }]);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      onClose();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_COMPANY_SPECIFIC_INDUSTRY] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={selectedCompanySpecificBusinessClassification ? 1200 : undefined}
      title={selectedCompanySpecificBusinessClassification ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={companySpecificBusinessClassificationSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField label="特定産業分野（企業）">
              <Typography variant="body14">{existingCompanySpecificIndustry}</Typography>
            </FormField>
          </Grid>
          <Grid item xs={6}></Grid>
          <Grid item xs={12}>
            <FormField control={control} name="companySpecificBusinessClassificationName">
              <Input />
            </FormField>
          </Grid>
          {selectedCompanySpecificBusinessClassification && (
            <Grid item xs={12}>
              <Stack gap={2}>
                <Typography variant="body14Bold">主たる技能</Typography>
                <CompanyMainSkillFormTable
                  companyMainSkillRows={companyMainSkillRows}
                  setCompanyMainSkillRows={setCompanyMainSkillRows}
                  existingCompanySpecificIndustryId={existingCompanySpecificIndustryId}
                  existingCompanySpecificBusinessClassificationId={toNumber(selectedCompanySpecificBusinessClassification.id)}
                  existingCompanySpecificBusinessClassificationName={
                    selectedCompanySpecificBusinessClassification.companySpecificBusinessClassificationName
                  }
                />
              </Stack>
            </Grid>
          )}
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
    </Modal>
  );
};
